// re.rejouis.fr — MES DEMANDES DE REPRISE : suivi de chaque demande du client
// connecté (étapes, bordereau Mondial Relay, suivi colis, crédit).
// Accessible par /suivi — lien « Mes demandes de reprise » du compte rejouis.fr.
// Lot dans le panier (2026-10-13) : le client y répond à l'offre de son lot
// (accepter / retirer → étiquette) et peut annuler tant que le colis n'est pas déposé.
import { useEffect, useState } from 'react';
import { EcranHeader, Icons, Illus, Spinner, Sticker, Thumb } from '../components/ui.jsx';
import { chargerDemandes, actionDemande } from '../lib/api.js';
import EtiquetteQr from '../components/EtiquetteQr.jsx';
import { fmtEuro } from '../lib/format.js';

const TERMINAUX = new Set(['Créditée', 'Refusée', 'Annulée']);
const ATTENTE_OFFRE = "En attente d'offre";
const OFFRE_A_VALIDER = 'Offre à valider';
const ligneLot = (d) => d.lignes.find((l) => l.lot) || null;
// Panier avec lot (nouveau circuit) ; les anciens lots (type « lot ») gardent leur suivi.
const avecLotPanier = (d) => d.type === 'mixte';
const AU_LABO = new Set(['Livrée', 'Ajustée', 'Créditée', 'Refusée']);
// Où en est le colis : étape calculée par le lab à partir du suivi Mondial Relay
// (lab _colis.js) — 0 à déposer · 1 déposé · 2 en route · 3 au relais du labo · 4 arrivé.
const etapeColis = (d) => (AU_LABO.has(d.statut) ? 4 : Number(d.etape_colis) || 0);
const colisDepose = (d) => etapeColis(d) >= 1;
const annulable = (d) => d.statut === ATTENTE_OFFRE || d.statut === OFFRE_A_VALIDER
  || (['Reçue', 'Offre envoyée'].includes(d.statut) && !colisDepose(d));
const ajouterJours = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString(); };

// Jouet non conforme à réception (pas propre, ou pas ce qui était déclaré) : crédit annulé,
// le client a DELAI_RETOUR_JOURS (date_limite_retour, calculée par le lab) pour le
// récupérer à ses frais ou nous le laisser. Sans réponse, on le garde.
const DELAI_RETOUR_JOURS = 5; // = lab _non-conforme.js
const MOTIFS_NC = { pas_propre: 'Pas arrivé propre', non_conforme: 'Ne correspond pas à ta déclaration' };
const delaiRetourPasse = (d) => !!d.date_limite_retour && new Date().toISOString().slice(0, 10) > d.date_limite_retour;
const choixRetourAttendu = (d) => !!d.date_non_conforme && !d.retour_choix && !delaiRetourPasse(d);

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
}

// Lot estimé sur photos, offre pas encore envoyée (pas de bordereau tant qu'on n'a pas chiffré).
const enEstimation = (d) => d.type === 'lot' && d.statut === 'Reçue' && !d.bordereau && !d.qr_code;

// Libellé + ton du badge résumé.
function resume(d) {
  if (choixRetourAttendu(d)) return { label: 'À toi de répondre', cls: 'badge-rouge' };
  if (d.statut === 'Annulée') return { label: 'Annulée', cls: 'badge-stone' };
  if (d.statut === ATTENTE_OFFRE) return { label: 'Offre du lot en cours', cls: 'badge-creme' };
  if (d.statut === OFFRE_A_VALIDER) return { label: 'À toi de répondre', cls: 'badge-rouge' };
  if (d.statut === 'Refusée') return { label: 'Refusée', cls: 'badge-stone' };
  if (d.statut === 'Créditée') return { label: 'Crédit versé', cls: 'badge-mint' };
  if (AU_LABO.has(d.statut)) return { label: 'Au labo', cls: 'badge-creme' };
  if (enEstimation(d)) return { label: 'Estimation en cours', cls: 'badge-creme' };
  if (colisDepose(d)) return { label: 'Colis en route', cls: 'badge-creme' };
  return { label: 'À expédier', cls: 'badge-rouge' };
}

// Étapes du suivi, chacune « fait » / « encours » / « afaire ».
function etapes(d) {
  const out = [{ t: 'Demande enregistrée', s: fmtDate(d.date_creation) ? `Le ${fmtDate(d.date_creation)}` : '', etat: 'fait' }];

  if (d.statut === 'Annulée') {
    out.push({ t: 'Demande annulée', s: 'N’envoie aucun colis pour cette demande.', etat: 'refus' });
    return out;
  }

  if (avecLotPanier(d)) {
    const lot = ligneLot(d);
    const e = lot?.etatLigne;
    if (d.statut === ATTENTE_OFFRE) out.push({ t: 'Estimation de ton lot', s: 'Offre sous 14 jours maximum, par e-mail et ici. N’envoie rien pour l’instant.', etat: 'encours' });
    else if (e === 'proposé') out.push({ t: 'Offre pour ton lot', s: `${fmtEuro(lot.creditEstime || 0)} proposés. À toi de répondre ci-dessous.`, etat: 'encours' });
    else if (e === 'validé') out.push({ t: 'Offre du lot acceptée', s: `${fmtEuro(lot.creditEstime || 0)} pour ton lot.`, etat: 'fait' });
    else if (e === 'retiré') out.push({ t: 'Lot retiré', s: 'Tu as retiré le lot de ta demande : ne l’envoie pas.', etat: 'fait' });
    else if (e === 'refusé') out.push({ t: 'Lot non repris', s: `${d.motif_lot ? d.motif_lot.trim() + ' ' : ''}Ne l’envoie pas : tu peux nous en faire don.`, etat: 'refus' });
  }

  if (d.type === 'lot') {
    const estime = !enEstimation(d);
    out.push({
      t: estime ? 'Offre envoyée' : 'Estimation de ton lot',
      s: estime ? `${fmtEuro(d.total_estime || 0)} proposés, bordereau joint.` : 'On regarde tes photos et on revient vers toi par e-mail.',
      etat: estime ? 'fait' : 'encours',
    });
  }

  const auLabo = AU_LABO.has(d.statut);
  const peutEnvoyer = !enEstimation(d) && d.statut !== ATTENTE_OFFRE && d.statut !== OFFRE_A_VALIDER;
  const etape = etapeColis(d);
  out.push(etape >= 1 ? {
    t: 'Colis déposé', s: 'Pris en charge par Mondial Relay.', etat: 'fait',
  } : {
    t: 'Dépose ton colis en point relais',
    s: peutEnvoyer ? (d.qr_code && !d.bordereau ? 'Présente ton QR code au point relais avec ton colis.' : 'Imprime ton bordereau, colle-le sur le carton.') : '',
    etat: peutEnvoyer ? 'encours' : 'afaire',
  });
  out.push({
    t: auLabo ? 'Colis arrivé au labo' : 'Colis en route vers le labo',
    s: auLabo ? '' : etape === 3 ? 'Arrivé près du labo, on va le chercher.'
      : etape >= 1 ? 'En général 3 à 5 jours ouvrés.' : '',
    etat: auLabo ? 'fait' : etape >= 1 ? 'encours' : 'afaire',
  });

  out.push({
    t: 'Vérification au labo',
    s: auLabo
      ? (d.date_livraison ? `Reçu le ${fmtDate(d.date_livraison)}.` : 'Ton colis est arrivé.')
        + (d.statut === 'Livrée' || d.statut === 'Ajustée' ? (d.date_credit_prevue && (d.total_estime || !d.virement) ? ` Crédit prévu le ${fmtDate(d.date_credit_prevue)}.` : '') : '')
      : 'Ton crédit est confirmé 2 jours après réception.',
    etat: TERMINAUX.has(d.statut) ? 'fait' : (auLabo ? 'encours' : 'afaire'),
  });

  if (d.date_non_conforme) {
    const n = d.lignes.filter((l) => l.nonConforme).length;
    out.push({
      t: n > 1 ? `${n} jouets non conformes` : 'Un jouet non conforme',
      s: 'Pas de crédit pour ' + (n > 1 ? 'ces jouets' : 'ce jouet') + (d.retour_choix === 'récupérer' ? ' · tu as choisi de le récupérer.'
        : d.retour_choix === 'laisser' ? ' · tu nous l’as laissé, on s’en occupe.'
        : delaiRetourPasse(d) ? ' · délai de réponse passé, on l’a gardé.' : ' · à toi de choisir ci-dessous.'),
      etat: 'refus',
    });
  }

  if (d.statut === 'Refusée' && d.date_non_conforme) {
    out.push({ t: 'Pas de crédit pour cette demande', s: 'Aucun jouet de ton colis n’a pu être repris.', etat: 'refus' });
  } else if (d.statut === 'Refusée') {
    out.push({ t: 'Demande refusée', s: d.motif_lot ? d.motif_lot.trim() : 'Tes jouets ne remplissaient pas les conditions de reprise. On t’a écrit pour t’expliquer.', etat: 'refus' });
  } else {
    const vir = d.virement?.montant || 0;
    const bon = (d.statut === 'Créditée' ? d.total_credite : d.total_estime) || 0;
    if (vir) {
      out.push({
        t: 'Virement sur ton compte bancaire',
        s: d.virement.paye_le
          ? `${fmtEuro(vir)} envoyés le ${fmtDate(d.virement.paye_le)}. Selon ta banque, il apparaît sous 1 à 3 jours ouvrés.`
          : `${fmtEuro(vir)} envoyés sur le compte que tu as indiqué, une fois tes jouets vérifiés.`,
        etat: d.virement.paye_le ? 'fait' : 'afaire',
      });
    }
    if (vir && !bon) return out;
    out.push({
      t: 'Crédit sur ton compte',
      s: d.statut === 'Créditée'
        ? `${fmtEuro(d.total_credite ?? d.total_estime ?? 0)} versés${d.date_credit_effectue ? ` le ${fmtDate(d.date_credit_effectue)}` : ''}, utilisables sur rejouis.fr.`
        : 'Versé en crédit boutique rejouis.fr, sans date limite.',
      etat: d.statut === 'Créditée' ? 'fait' : 'afaire',
    });
  }
  return out;
}

function Etapes({ liste }) {
  return liste.map((e, i, arr) => (
    <div key={i} style={{ display: 'flex', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className={'step-dot ' + (e.etat === 'refus' ? 'afaire' : e.etat)}>
          {e.etat === 'fait' ? <Icons.check size={13} sw={2.6} /> : e.etat === 'refus' ? <Icons.x size={13} sw={2.6} /> : i + 1}
        </div>
        {i < arr.length - 1 ? <div style={{ width: 2, flex: 1, minHeight: 14, background: e.etat === 'fait' ? 'var(--bleu-pale)' : 'var(--bord)', margin: '4px 0' }} /> : null}
      </div>
      <div style={{ paddingBottom: i < arr.length - 1 ? 12 : 0, opacity: e.etat === 'afaire' ? 0.6 : 1 }}>
        <div style={{ fontWeight: e.etat === 'encours' ? 700 : 600, fontSize: 13.5 }}>{e.t}</div>
        {e.s ? <div style={{ fontSize: 12, color: 'var(--stone)', lineHeight: 1.5 }}>{e.s}</div> : null}
      </div>
    </div>
  ));
}

function etatJouet(l) {
  if (l.nonConforme) return MOTIFS_NC[l.nonConforme] || 'Non conforme';
  if (l.etatLigne === 'retiré') return 'Retiré de la demande';
  if (l.refuse) return 'Non repris';
  if (l.lot) return l.etatLigne === 'en attente' ? 'Offre en cours' : l.etatLigne === 'proposé' ? 'Offre proposée' : 'Offre acceptée';
  return l.etat;
}

// Contenu de la demande, toujours visible (décision Benoni 2026-10-13) : chaque jouet
// avec sa photo, et le lot avec sa description et les photos envoyées — pour que le
// client mette dans son colis EXACTEMENT ce qu'il a validé, sans rien oublier.
// Les lignes refusées / retirées restent affichées, barrées : « ne l'envoie pas ».
function ContenuDemande({ d }) {
  if (!d.lignes.length) return null;
  const colisFige = Boolean(d.bordereau || d.qr_code) && !AU_LABO.has(d.statut) && !TERMINAUX.has(d.statut);
  const photosLot = (d.photos || []).map((p) => ({ src: p.vignette, lien: p.url }));
  const n = d.lignes.filter((l) => !l.refuse || l.nonConforme).reduce((s, l) => s + (l.quantite || 1), 0);
  return (
    <div style={{ marginTop: 16, padding: 14, borderRadius: 14, background: 'var(--creme-fonce)' }}>
      <div className="t-overline" style={{ marginBottom: 10 }}>
        {colisFige ? `À mettre dans ton colis · ${n} jouet${n > 1 ? 's' : ''}` : `Ce que contient ta demande · ${n} jouet${n > 1 ? 's' : ''}`}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {d.lignes.map((l, i) => {
          const credit = l.creditFinal ?? l.creditEstime;
          const montrerCredit = credit != null && !l.refuse && !(l.lot && l.etatLigne === 'en attente');
          const barre = l.refuse ? { opacity: 0.55 } : null;
          return (
            <div key={i} style={{ background: 'var(--blanc)', borderRadius: 12, padding: 10, ...barre }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {l.lot
                  ? (photosLot[0] ? <img className="ph-no-capture" src={photosLot[0].src} alt="" width={56} height={56} style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} /> : <Illus nom="lot" size={56} />)
                  : <Thumb produit={l} size={56} radius={12} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, textDecoration: l.refuse ? 'line-through' : 'none' }}>
                    {l.quantite > 1 && !l.lot ? `${l.quantite} × ` : ''}{l.titre}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 1 }}>
                    {[l.marque, etatJouet(l)].filter(Boolean).join(' · ')}
                    {l.refuse && !l.nonConforme && !AU_LABO.has(d.statut) ? ' · ne l’envoie pas' : ''}
                  </div>
                  {l.noteNonConforme ? <div style={{ fontSize: 12, color: 'var(--rouge-sombre)', marginTop: 2 }}>{l.noteNonConforme}</div> : null}
                </div>
                {montrerCredit ? (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--rouge)' }}>{fmtEuro(l.virement ? l.virement : credit)}</div>
                    {l.virement ? <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--stone)' }}>par virement</div> : null}
                  </div>
                ) : null}
              </div>
              {l.lot && (l.description || photosLot.length) ? (
                <div style={{ marginTop: 10 }}>
                  {l.description ? (
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--encre)', whiteSpace: 'pre-wrap', padding: '8px 10px', borderLeft: '3px solid var(--jaune)', background: 'var(--jaune-pale)', borderRadius: 8 }}>
                      {l.description}
                    </div>
                  ) : null}
                  {photosLot.length ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {photosLot.map((p, j) => (
                        <a key={j} href={p.lien} target="_blank" rel="noopener noreferrer">
                          <img className="ph-no-capture" src={p.src} alt={`Photo ${j + 1} du lot`} loading="lazy" width={84} height={84}
                            style={{ width: 84, height: 84, borderRadius: 10, objectFit: 'cover', display: 'block', background: 'var(--creme-fonce)' }} />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Montant à afficher (versé / ajusté / estimé), null quand il n'a pas de sens.
function montantDe(d) {
  if (d.statut === 'Refusée' || d.statut === 'Annulée' || enEstimation(d)) return null;
  if (d.statut === ATTENTE_OFFRE) return d.total_estime ? { m: d.total_estime, label: '+ offre du lot' } : null;
  if (d.statut === OFFRE_A_VALIDER) return d.total_estime ? { m: d.total_estime, label: 'avec l’offre du lot' } : null;
  const m = d.statut === 'Créditée' || d.statut === 'Ajustée' ? d.total_credite : d.total_estime;
  const vir = d.virement?.montant || 0;
  // Tout en virement : le montant affiché est celui du virement.
  if (vir && !m) return { m: vir, label: d.virement.paye_le ? 'virés' : 'par virement' };
  return m == null ? null : {
    m, vir,
    label: d.statut === 'Créditée' ? 'versés' : d.statut === 'Ajustée' ? 'après vérification' : 'estimés',
  };
}

// Version réduite (liste de « Mon compte ») : vignette, ref, état, montant. Clic → détail.
export function DemandeReduite({ d, onOuvrir }) {
  const r = resume(d);
  const montant = montantDe(d);
  const vignette = d.photos?.[0]?.vignette || d.lignes.find((l) => l.image)?.image || null;
  const n = d.lignes.reduce((s, l) => s + (l.quantite || 1), 0);
  return (
    <button onClick={onOuvrir} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: 12 }}>
      {vignette
        ? <img className="ph-no-capture" src={vignette} alt="" width={46} height={46} style={{ width: 46, height: 46, borderRadius: 12, objectFit: 'cover', flexShrink: 0, background: 'var(--creme-fonce)' }} />
        : <Illus nom={TERMINAUX.has(d.statut) ? 'bon' : 'lot'} size={46} />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontWeight: 700, fontSize: 13.5 }}>{d.ref}</span>
          <span className={'badge ' + r.cls}>{r.label}</span>
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--stone)', marginTop: 2 }}>
          {[fmtDate(d.date_creation), n ? `${n} jouet${n > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ')}
        </span>
      </span>
      {montant ? <span className="t-display" style={{ fontSize: 15, color: 'var(--rouge)', flexShrink: 0 }}>{fmtEuro(montant.m)}</span> : null}
      <Icons.chev size={18} style={{ color: 'var(--stone)', flexShrink: 0 }} />
    </button>
  );
}

// Réponse du client : offre du lot (accepter / retirer), retrait anticipé du lot,
// annulation. Chaque action est confirmée : elle fige le contenu du colis.
function ActionsDemande({ d, onMaj }) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState(null);
  const lot = ligneLot(d);
  const reconnus = d.lignes.filter((l) => !l.lot && !l.refuse);
  const totalJouets = reconnus.reduce((s, l) => s + (l.creditFinal ?? l.creditEstime ?? 0), 0);
  const offre = d.statut === OFFRE_A_VALIDER && lot;
  const attente = d.statut === ATTENTE_OFFRE && lot;
  if (!offre && !attente && !annulable(d)) return null;

  async function agir(action, confirmation) {
    if (!window.confirm(confirmation)) return;
    setEnCours(action);
    setErreur(null);
    try {
      await actionDemande(action, d.ref);
      await onMaj();
    } catch (e) {
      setErreur(e.message || 'Une erreur est survenue, réessaie dans un instant.');
    } finally {
      setEnCours(null);
    }
  }
  const limite = d.date_offre ? fmtDate(ajouterJours(d.date_offre, 14)) : '';
  const charge = (a, texte) => (enCours === a ? <Spinner size={16} /> : texte);
  const montantLot = lot?.creditEstime || 0;

  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {offre ? (
        <div style={{ padding: '14px 16px', borderRadius: 14, background: 'var(--jaune-pale)', border: '2px solid var(--jaune)', color: 'var(--encre)' }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Notre offre pour ton lot : {fmtEuro(montantLot)}</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 4 }}>
            {reconnus.length ? <>Avec tes autres jouets : <strong>{fmtEuro(totalJouets + montantLot)}</strong> au total. </> : null}
            Réponds {limite ? `avant le ${limite}` : 'sous 14 jours'}, sinon la demande sera annulée. Ton étiquette arrive dès ta réponse.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" style={{ flex: '1 1 180px' }} disabled={!!enCours}
              onClick={() => agir('accepter_lot', `Accepter ${fmtEuro(montantLot)} pour ton lot ?\n\nTon colis devra contenir ${reconnus.length ? 'tes jouets ET ton lot' : 'ton lot'}. On génère ton étiquette tout de suite.`)}>
              {charge('accepter_lot', 'J’accepte l’offre du lot')}
            </button>
            <button className="btn btn-secondary" style={{ flex: '1 1 180px' }} disabled={!!enCours}
              onClick={() => agir('retirer_lot', reconnus.length
                ? 'Retirer le lot ?\n\nTon colis ne devra contenir QUE tes autres jouets : n’envoie pas le lot. On génère ton étiquette tout de suite.'
                : 'Retirer le lot ?\n\nC’est ton seul envoi : ta demande sera annulée.')}>
              {charge('retirer_lot', 'Je retire le lot')}
            </button>
          </div>
        </div>
      ) : null}
      {attente && reconnus.length ? (
        <button className="btn btn-secondary btn-block" disabled={!!enCours}
          onClick={() => agir('retirer_lot', 'Retirer le lot sans attendre notre offre ?\n\nTu reçois tout de suite l’étiquette pour tes autres jouets. N’envoie pas le lot.')}>
          {charge('retirer_lot', 'Retirer le lot et recevoir mon étiquette')}
        </button>
      ) : null}
      {annulable(d) ? (
        <button className="btn btn-tertiary btn-block" disabled={!!enCours}
          onClick={() => agir('annuler', `Annuler toute la demande ${d.ref} ?\n\nN’envoie alors aucun colis${d.bordereau || d.qr_code ? ' : l’étiquette ne servira plus' : ''}.`)}>
          {charge('annuler', 'Annuler ma demande')}
        </button>
      ) : null}
      {erreur ? <div style={{ fontSize: 12.5, color: 'var(--rouge-sombre)', lineHeight: 1.5 }}>{erreur}</div> : null}
    </div>
  );
}

// Jouet non conforme : récupérer (on crée la commande de renvoi, le client paie la
// livraison au checkout rejouis.fr) ou nous le laisser. Choix définitif.
function RetourNonConforme({ d, onMaj }) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState(null);
  if (!d.date_non_conforme) return null;
  const n = d.lignes.filter((l) => l.nonConforme).length;
  const le = n > 1 ? 'les' : 'le';
  const limite = fmtDate(d.date_limite_retour);
  const cadre = { marginTop: 16, padding: '14px 16px', borderRadius: 14, background: 'var(--jaune-pale)', border: '2px solid var(--jaune)', color: 'var(--encre)', fontSize: 12.5, lineHeight: 1.55 };

  async function choisir(action, confirmation) {
    if (!window.confirm(confirmation)) return;
    setEnCours(action);
    setErreur(null);
    try {
      const r = await actionDemande(action, d.ref);
      if (r?.lien) window.location.href = r.lien;
      else await onMaj();
    } catch (e) {
      setErreur(e.message || 'Une erreur est survenue, réessaie dans un instant.');
      // Affichage peut-être périmé (choix fait ailleurs) : on relit l'état réel.
      if (e.status === 409) onMaj().catch(() => {});
    } finally {
      setEnCours(null);
    }
  }

  if (d.retour_choix === 'récupérer') {
    return (
      <div style={cadre}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>Retour de ton jouet</div>
        {d.retour_lien ? (
          <>
            <div style={{ marginTop: 4 }}>Règle les frais de livraison : on te {le} renvoie dès réception du paiement.</div>
            <a className="btn btn-primary btn-block" href={d.retour_lien} style={{ marginTop: 12, textDecoration: 'none' }}>Payer la livraison</a>
          </>
        ) : (
          <div style={{ marginTop: 4 }}>C’est noté : on t’envoie le lien de paiement des frais de livraison par e-mail, puis on te {le} renvoie.</div>
        )}
        {/* Changement d'avis tant que la livraison n'est pas payée (le lab refuse sinon). */}
        {!delaiRetourPasse(d) ? (
          <button className="btn btn-tertiary btn-block" style={{ marginTop: 8 }} disabled={!!enCours}
            onClick={() => choisir('laisser_jouets', `Finalement, nous laisser ${n > 1 ? 'tes jouets' : 'ton jouet'} ?\n\nLa commande de renvoi est annulée, tu n’as rien à payer. On s’en occupe (recyclage). Ce choix est définitif.`)}>
            {enCours === 'laisser_jouets' ? <Spinner size={16} /> : `Finalement, je vous ${le} laisse`}
          </button>
        ) : null}
        {erreur ? <div style={{ fontSize: 12.5, color: 'var(--rouge-sombre)', marginTop: 8 }}>{erreur}</div> : null}
      </div>
    );
  }
  if (d.retour_choix === 'laisser') return null;
  if (delaiRetourPasse(d)) {
    return <div style={{ ...cadre, background: 'var(--creme-fonce)', border: 'none' }}>Le délai de {DELAI_RETOUR_JOURS} jours pour {le} récupérer est passé : on {le} a gardé{n > 1 ? 's' : ''}. Un souci ? Écris-nous.</div>;
  }
  return (
    <div style={cadre}>
      <div style={{ fontWeight: 800, fontSize: 15 }}>{n > 1 ? 'Tes jouets ne peuvent pas être repris' : 'Ton jouet ne peut pas être repris'}</div>
      <div style={{ marginTop: 4 }}>
        À la vérification, {n > 1 ? 'ils ne correspondaient' : 'il ne correspondait'} pas à ta déclaration (détail ci-dessus) : pas de crédit pour {n > 1 ? 'eux' : 'lui'}.
        Tu peux {le} récupérer (les frais de retour sont à ta charge) ou nous {le} laisser.
        Tu as {DELAI_RETOUR_JOURS} jours pour répondre{limite ? `, jusqu’au ${limite} inclus` : ''} ; sans réponse, on {le} garde.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" style={{ flex: '1 1 180px' }} disabled={!!enCours}
          onClick={() => choisir('recuperer_jouets', `Récupérer ${n > 1 ? 'tes jouets' : 'ton jouet'} ?\n\nTu vas payer la livraison sur rejouis.fr (choix du point relais ou de l’adresse), puis on ${le} renvoie.`)}>
          {enCours === 'recuperer_jouets' ? <Spinner size={16} /> : `Je ${le} récupère`}
        </button>
        <button className="btn btn-secondary" style={{ flex: '1 1 180px' }} disabled={!!enCours}
          onClick={() => choisir('laisser_jouets', `Nous laisser ${n > 1 ? 'tes jouets' : 'ton jouet'} ?\n\nOn s’en occupe (recyclage). Ce choix est définitif.`)}>
          {enCours === 'laisser_jouets' ? <Spinner size={16} /> : `Je vous ${le} laisse`}
        </button>
      </div>
      {erreur ? <div style={{ fontSize: 12.5, color: 'var(--rouge-sombre)', marginTop: 8 }}>{erreur}</div> : null}
    </div>
  );
}

function CarteDemande({ d, onMaj }) {
  const r = resume(d);
  const fini = TERMINAUX.has(d.statut);
  const montant = montantDe(d);
  // Le bordereau ne sert plus une fois le colis arrivé.
  const montrerBordereau = d.bordereau && !colisDepose(d);
  // QR code Mondial Relay (défaut depuis le 2026-10-13), tant que le colis n'est pas déposé.
  const montrerQr = d.qr_code && !d.bordereau && !colisDepose(d) && !TERMINAUX.has(d.statut);

  return (
    <div id={`demande-${d.ref}`} className="card" style={{ padding: 16, scrollMarginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Illus nom={fini ? 'bon' : colisDepose(d) ? 'camion' : 'lot'} size={46} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontWeight: 700, fontSize: 14 }}>{d.ref}</span>
            <span className={'badge ' + r.cls}>{r.label}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 3 }}>
            {fmtDate(d.date_creation) ? `Créée le ${fmtDate(d.date_creation)}` : ''}
            {d.type === 'lot' ? ' · lot' : avecLotPanier(d) ? ' · avec un lot' : ''}
          </div>
        </div>
        {montant ? (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div className="t-display" style={{ fontSize: 18, color: 'var(--rouge)' }}>{fmtEuro(montant.m)}</div>
            <div style={{ fontSize: 11, color: 'var(--stone)' }}>{montant.label}{montant.vir ? ' en bon d’achat' : ''}</div>
            {montant.vir ? <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--noir)', marginTop: 2 }}>+ {fmtEuro(montant.vir)} par virement</div> : null}
          </div>
        ) : null}
      </div>

      <ContenuDemande d={d} />

      <div style={{ marginTop: 16 }}><Etapes liste={etapes(d)} /></div>

      {montrerQr ? (
        <div style={{ marginTop: 16, padding: 14, borderRadius: 14, border: '1px solid var(--bord)' }}>
          <EtiquetteQr qrCode={d.qr_code} refDemande={d.ref} onPdf={onMaj} />
        </div>
      ) : null}

      {montrerBordereau || d.suivi_url ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {montrerBordereau ? (
            <a className="btn btn-primary" href={d.bordereau} target="_blank" rel="noopener noreferrer" style={{ flex: '1 1 180px', textDecoration: 'none' }}>
              <Icons.download size={16} /> Mon bordereau
            </a>
          ) : null}
          {d.suivi_url ? (
            <a className="btn btn-secondary" href={d.suivi_url} target="_blank" rel="noopener noreferrer" style={{ flex: '1 1 180px', textDecoration: 'none' }}>
              <Icons.truck size={16} /> Suivre le colis
            </a>
          ) : null}
        </div>
      ) : null}
      {d.tracking ? (
        <div style={{ fontSize: 11.5, color: 'var(--stone)', marginTop: 8 }}>
          N° d’expédition Mondial Relay : <span className="mono" style={{ color: 'var(--noir)' }}>{d.tracking}</span>
        </div>
      ) : null}

      <RetourNonConforme d={d} onMaj={onMaj} />
      <ActionsDemande d={d} onMaj={onMaj} />

    </div>
  );
}

export default function MesDemandes({ go, client, onSeConnecter, refOuverte }) {
  const [demandes, setDemandes] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!client) return;
    let annule = false;
    chargerDemandes()
      .then((d) => { if (!annule) setDemandes(d); })
      .catch((e) => { if (!annule) setErreur(e.message); });
    return () => { annule = true; };
  }, [client]);

  // Arrivée depuis la liste réduite de « Mon compte » → on se place sur la demande cliquée.
  useEffect(() => {
    if (demandes && refOuverte) document.getElementById(`demande-${refOuverte}`)?.scrollIntoView({ block: 'start' });
  }, [demandes, refOuverte]);

  // Après une action (offre acceptée, lot retiré, annulation) : relecture forcée.
  const recharger = () => chargerDemandes({ force: true }).then(setDemandes).catch((e) => setErreur(e.message));

  // Retour arrière depuis une page externe (paiement du renvoi…) : le navigateur restaure
  // la page telle qu'elle était (cache bfcache) → on relit l'état réel des demandes.
  useEffect(() => {
    if (!client) return undefined;
    const auRetour = (e) => { if (e.persisted) recharger(); };
    window.addEventListener('pageshow', auRetour);
    return () => window.removeEventListener('pageshow', auRetour);
  }, [client]);

  // Une demande close qui attend le choix du client (jouet non conforme) reste « en cours ».
  const terminee = (d) => TERMINAUX.has(d.statut) && !choixRetourAttendu(d);
  const enCours = (demandes || []).filter((d) => !terminee(d));
  const terminees = (demandes || []).filter(terminee);

  return (
    <div className="no-scrollbar anim-fade" style={{ height: '100%', overflow: 'auto', padding: '8px 0 40px' }}>
      <EcranHeader titre="Mes demandes de reprise" onBack={() => go('accueil')} />

      {!client ? (
        <div style={{ padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Sticker nom="boite" size={84} rot={-6} anim="pop" />
          <h2 className="t-display" style={{ fontSize: 24, margin: 0 }}>Connecte-toi pour suivre<br /><span style={{ color: 'var(--rouge)' }}>tes reprises</span></h2>
          <p style={{ fontSize: 13.5, color: 'var(--stone)', lineHeight: 1.6, margin: 0 }}>
            Avec ton compte rejouis.fr : bordereau, suivi du colis et crédit, tout est ici.
          </p>
          <button className="btn btn-primary btn-block" onClick={onSeConnecter}>Se connecter avec Rejouis</button>
        </div>
      ) : erreur ? (
        <div style={{ padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="soft-card" style={{ padding: 14, fontSize: 13, color: 'var(--encre-doux)', lineHeight: 1.5 }}>
            Impossible de charger tes demandes pour l’instant. {erreur}
          </div>
          <button className="btn btn-secondary btn-block" onClick={() => { setErreur(null); setDemandes(null); chargerDemandes({ force: true }).then(setDemandes).catch((e) => setErreur(e.message)); }}>Réessayer</button>
        </div>
      ) : demandes == null ? (
        <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner size={20} label="Chargement de tes demandes…" /></div>
      ) : demandes.length === 0 ? (
        <div style={{ padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Sticker nom="oeuf-etincelle" size={84} rot={6} anim="pop" />
          <h2 className="t-display" style={{ fontSize: 24, margin: 0 }}>Pas encore de reprise</h2>
          <p style={{ fontSize: 13.5, color: 'var(--stone)', lineHeight: 1.6, margin: 0 }}>
            Revends un jouet dont tu ne te sers plus : tu reçois du crédit sur rejouis.fr, et on lui offre une seconde vie.
          </p>
          <button className="btn btn-primary btn-block" onClick={() => go('accueil')}>Estimer un jouet</button>
        </div>
      ) : (
        <div style={{ padding: '4px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {enCours.length ? <div className="t-overline" style={{ padding: '0 2px' }}>En cours</div> : null}
          {enCours.map((d) => <CarteDemande key={d.ref} d={d} onMaj={recharger} />)}
          {terminees.length ? <div className="t-overline" style={{ padding: '8px 2px 0' }}>Terminées</div> : null}
          {terminees.map((d) => <CarteDemande key={d.ref} d={d} onMaj={recharger} />)}
          <button className="btn btn-tertiary btn-block" onClick={() => go('accueil')} style={{ marginTop: 4 }}>Revendre un autre jouet</button>
        </div>
      )}
    </div>
  );
}
